import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
/**
 * Laboratory values for one patient, normalised across the three places a
 * value can come from: history imported from a previous EMR, a result a
 * partner laboratory delivered against an order, and a value read out of a
 * filed document.
 *
 * The read model never converts a unit and never merges two analytes that only
 * look alike: a series is keyed by analyte and unit, and an analyte reported in
 * two units is two series, named in `unitVariants`. A value the extraction
 * could not stand behind is carried as text so it stays visible on the chart
 * and stays out of every calculation.
 *
 * Mirrors api.list_patient_lab_results.
 */
export const LabResultSourceKindSchema = z.enum(["migrated", "partner", "document"]);
export const LabResultInterpretationSchema = z.enum(["normal", "abnormal", "critical", "unknown"]);
export const LabResultStatusSchema = z.enum(["preliminary", "final", "corrected", "unknown"]);
export const LabResultQualitySchema = z.enum(["supported", "uncertain", "unreadable"]);
export const LabResultCodeSystemSchema = z.enum(["LOINC", "RadLex", "local", "free_text"]);
export const LabResultSourceSchema = z
    .object({
    kind: LabResultSourceKindSchema,
    id: z.string().min(1),
    reportId: z.string().max(240).nullish(),
    version: z.number().int().nonnegative().optional(),
    /** Imported values: who signed the result off at the previous clinic. */
    reviewedBy: z.string().max(160).optional(),
    reviewedAt: IsoDateTimeSchema.optional(),
    /** Partner values: where the result sits in the order workflow. */
    workflowState: z.string().max(80).optional(),
    /** Document values: the report and its review state. */
    incomingReportId: IdSchema.optional(),
    reviewStatus: z.string().max(40).optional(),
    quality: LabResultQualitySchema.optional(),
    valueIndex: z.number().int().nonnegative().optional(),
    page: z.coerce.number().int().positive().optional(),
})
    .passthrough();
export const LabResultValueSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("numeric"),
        value: z.number(),
        unit: z.string().max(80).optional(),
    })
        .strict(),
    z.object({ kind: z.literal("text"), text: z.string().max(4000) }).strict(),
]);
export const LabResultRowSchema = z
    .object({
    id: z.string().min(1),
    /** Stable grouping key; exact equality is the only match across sources. */
    analyteKey: z.string().min(1).max(320),
    code: z.string().max(120).nullish(),
    codeSystem: LabResultCodeSystemSchema.nullish(),
    display: z.string().min(1).max(240),
    labName: z.string().max(160).nullish(),
    reportTitle: z.string().max(240).nullish(),
    panelTitle: z.string().max(240).nullish(),
    value: LabResultValueSchema,
    unit: z.string().max(80).nullish(),
    referenceLow: z.number().nullish(),
    referenceHigh: z.number().nullish(),
    referenceText: z.string().max(500).nullish(),
    interpretation: LabResultInterpretationSchema,
    /** The flag exactly as the laboratory printed it, when there was one. */
    sourceFlag: z.string().max(16).nullish(),
    quality: LabResultQualitySchema.nullish(),
    resultStatus: LabResultStatusSchema,
    collectedAt: IsoDateTimeSchema,
    /** `day` when the source gave a date and no time; never invent a time. */
    collectedAtPrecision: z.enum(["instant", "day"]),
    reportedAt: IsoDateTimeSchema.nullish(),
    source: LabResultSourceSchema,
    provenance: z
        .object({
        sourceType: z.string().min(1).max(80),
        sourceId: IdSchema,
        version: z.number().int().nonnegative().optional(),
        contentHash: Sha256Schema.optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough()
    .superRefine((row, context) => {
    if (row.referenceLow !== null &&
        row.referenceLow !== undefined &&
        row.referenceHigh !== null &&
        row.referenceHigh !== undefined &&
        row.referenceHigh < row.referenceLow) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["referenceHigh"],
            message: "referenceHigh must not be below referenceLow",
        });
    }
});
export const PatientLabResultsSchema = z
    .object({
    patientId: IdSchema,
    siteId: IdSchema,
    items: z.array(LabResultRowSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
})
    .passthrough();
export const LabTrendPointSchema = z
    .object({
    collectedAt: IsoDateTimeSchema,
    collectedAtPrecision: z.enum(["instant", "day"]),
    value: z.number(),
    interpretation: LabResultInterpretationSchema,
    source: LabResultSourceSchema,
})
    .passthrough();
export const LabTrendSeriesSchema = z
    .object({
    analyteKey: z.string().min(1).max(320),
    /** Analyte and unit together: two units are never one line. */
    seriesKey: z.string().min(1),
    display: z.string().min(1).max(240),
    codeSystem: LabResultCodeSystemSchema.nullish(),
    code: z.string().max(120).nullish(),
    labName: z.string().max(160).nullish(),
    unit: z.string().max(80).nullish(),
    referenceLow: z.number().nullish(),
    referenceHigh: z.number().nullish(),
    pointCount: z.number().int().nonnegative(),
    truncated: z.boolean().default(false),
    /** Oldest first, so a line reads left to right. */
    points: z.array(LabTrendPointSchema),
})
    .passthrough();
export const PatientLabTrendsSchema = z
    .object({
    patientId: IdSchema,
    siteId: IdSchema,
    series: z.array(LabTrendSeriesSchema),
    /** Analytes the source reported in more than one unit, named rather than merged. */
    unitVariants: z.array(z.object({ analyteKey: z.string().min(1), units: z.array(z.string()) }).passthrough()),
})
    .passthrough();
/** Mirrors the p_options allow-list of api.list_patient_lab_results. */
export const PatientLabResultsOptionsSchema = z
    .object({
    mode: z.enum(["list", "trend"]).optional(),
    analyteKeys: z.array(z.string().min(1)).max(50).optional(),
    sources: z.array(LabResultSourceKindSchema).optional(),
    from: IsoDateTimeSchema.optional(),
    to: IsoDateTimeSchema.optional(),
    limit: z.number().int().min(1).max(500).optional(),
    offset: z.number().int().min(0).max(10_000).optional(),
    maxPoints: z.number().int().min(1).max(500).optional(),
})
    .strict();
//# sourceMappingURL=patient-lab-results.js.map